import { useTranslation } from "react-i18next";
import {
  AwardCriterionType,
  AwardSubCriterionAnswerFormat,
  Currency,
  type BidV2AwardCriterionResponseDto,
  type BidV2Dto,
  type BidV2PatchDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import {
  groupScorableAwardRows,
  scorableAwardRows,
  type ScorableAwardRow,
} from "../../../../common/award-criteria/scorableAwardRows";
import { useEditor } from "../../../../common/editing/save-coordinator";
import { isAwardCriterionAnswerComplete } from "./AwardCriterionAnswerCard";
import { isBidV2Editable } from "./publicBidEditability";

/**
 * The tendered price is one figure the price model scores for every price row, so it is asked for once.
 * A competition awarded on the lowest price has no criteria at all, and one whose criteria happen to
 * carry no price row still needs the figure — both get a standalone price card. An event that does not
 * collect a price drops its price rows instead of asking for something it will not use.
 */
const SYNTHETIC_PRICE_ROW_ID = "price";

/**
 * One answer, with the fields the patch does not mention carried over from what is already stored.
 *
 * The bid is saved with a merge patch, and inside a list the merge walker replaces elements rather
 * than merging them — so a field left out of this object does not mean "unchanged", it reaches the
 * BFF as null and clears what the supplier wrote. Every field of the response has to be listed here,
 * which is exactly the kind of rule that is quietly broken by adding a field somewhere else.
 */
export const mergeAwardCriterionResponse = (
  current: BidV2AwardCriterionResponseDto | undefined,
  criterionId: string,
  patch: Partial<BidV2AwardCriterionResponseDto>,
): BidV2AwardCriterionResponseDto => ({
  criterionId,
  text: current?.text ?? null,
  number: current?.number ?? null,
  choice: current?.choice ?? null,
  partialElaboration: current?.partialElaboration ?? null,
  attachmentIds: current?.attachmentIds ?? [],
  ...patch,
});

const withPriceRow = (rows: ScorableAwardRow[], collectsPrice: boolean, priceLabel: string): ScorableAwardRow[] => {
  if (!collectsPrice) return rows.filter((row) => !row.isPrice);
  if (rows.some((row) => row.isPrice)) return rows;
  return [
    {
      id: SYNTHETIC_PRICE_ROW_ID,
      parentId: null,
      groupLabel: priceLabel,
      groupWeightPercent: null,
      type: AwardCriterionType.Price,
      name: priceLabel,
      description: "",
      ownWeightPercent: null,
      effectiveWeightPercent: null,
      answerFormat: AwardSubCriterionAnswerFormat.Number,
      requiresDocumentation: false,
      isPrice: true,
    },
    ...rows,
  ];
};

/**
 * The supplier's side of the award criteria. The rows come from the event, the answers from the bid,
 * and the price rows write the tendered price rather than a response of their own — the evaluation
 * scores every price row from that single figure.
 */
export const useAwardCriterionAnswers = (bidId: string, event: PortalSourcingEventDetailDto) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<BidV2Dto, BidV2PatchDto>(bidId);
  const collectsPrice = event.collectSupplierPrice ?? true;
  const rows = withPriceRow(
    scorableAwardRows(event.awardCriteria?.criteria, (type: AwardCriterionType) => t(type)),
    collectsPrice,
    t("Price"),
  );
  const responses = state.awardCriterionResponses ?? [];
  const currency = event.estimatedValue?.currency ?? Currency.Nok;
  const price = state.price?.value ?? null;
  const readOnly = !isBidV2Editable(state);

  const responseFor = (row: ScorableAwardRow) => responses.find((response) => response.criterionId === row.id);

  const upsert = (row: ScorableAwardRow, patch: Partial<BidV2AwardCriterionResponseDto>) => {
    if (readOnly) return;
    // A price row's answer is the tendered price, and the synthetic one has no criterion id to key a
    // response by — sending one would be rejected server-side.
    if (row.isPrice) return;
    const next = mergeAwardCriterionResponse(responseFor(row), row.id, patch);
    updateField(
      { awardCriterionResponses: [...responses.filter((r) => r.criterionId !== row.id), next] },
      { debounceMs: 300 },
    );
  };

  const setPrice = (value: number | null) => {
    if (readOnly) return;
    updateField({ price: { value, currency } }, { debounceMs: 400 });
  };

  return {
    rows,
    groups: groupScorableAwardRows(rows),
    responseFor,
    upsert,
    price,
    currency,
    setPrice,
    answeredCount: rows.filter((row) => isAwardCriterionAnswerComplete(row, responseFor(row), price)).length,
    isSaving:
      fieldStatus("awardCriterionResponses") === "saving" ||
      fieldStatus("awardCriterionResponses") === "pending" ||
      fieldStatus("price") === "saving" ||
      fieldStatus("price") === "pending",
    readOnly,
  };
};
